import { CreateProductInput, ProductKind, VatRate } from "@cleanbudget/contract/product";
import { Card, CardContent } from "@cleanbudget/ui/components/card";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { z } from "zod";
import { BackLink } from "@/components/back-link";
import {
	Form,
	FormError,
	SelectField,
	SubmitButton,
	TextareaField,
	TextField,
	useZodForm,
} from "@/components/form/form";
import { PageHeader } from "@/components/page-header";
import { apiErrorTag } from "@/lib/api-error";
import { orpc } from "@/lib/orpc";
import { MoneyText, VAT_RATES } from "./-components/fields";
import { useProductErrorMessage } from "./-components/product-errors";
import { slugify } from "./-components/slug";

export const Route = createFileRoute("/_admin/product/new")({
	component: NewProductPage,
});

/** The contract's new product, with the price as typed and the VAT rate as the select gives it. */
const NewProductForm = CreateProductInput.extend({
	consumerPrice: MoneyText,
	vatRate: z.coerce.number().pipe(VatRate),
});

/**
 * Nieuw product (#48): name and price first, the slug proposed from the name under "Meer". The
 * product starts with one variant; the editor opens on it right away, where options, variants and
 * everything else follow.
 */
function NewProductPage() {
	const { t } = useTranslation("product");
	const { t: tCommon } = useTranslation();
	const { t: tBundle } = useTranslation("bundle");
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const errorMessage = useProductErrorMessage();
	const [moreOpen, setMoreOpen] = useState(false);
	const form = useZodForm(NewProductForm, {
		defaultValues: {
			kind: "standard",
			name: "",
			slug: "",
			description: "",
			vatRate: "21",
			consumerPrice: "",
			active: true,
		},
	});
	const create = useMutation(orpc.product.create.mutationOptions());

	// The slug follows the name until someone types a slug of their own.
	const name = useWatch({ control: form.control, name: "name" });
	const { dirtyFields, errors } = useFormState({ control: form.control });
	useEffect(() => {
		if (!dirtyFields.slug) form.setValue("slug", slugify(name ?? ""));
	}, [name, dirtyFields.slug, form]);
	const slug = useWatch({ control: form.control, name: "slug" });

	const onSubmit = async (values: z.output<typeof NewProductForm>) => {
		try {
			const product = await create.mutateAsync(values);
			queryClient.setQueryData(orpc.product.get.queryKey({ input: { productId: product.id } }), product);
			await queryClient.invalidateQueries({ queryKey: orpc.product.list.key() });
			await navigate({ to: "/product/$productId", params: { productId: product.id } });
		} catch (error) {
			match(apiErrorTag(error))
				.with("SlugTaken", () => {
					setMoreOpen(true);
					form.setError("slug", { type: "server", message: t("error.slugTaken") });
				})
				.otherwise(() => form.setError("root", { type: "server", message: errorMessage(error) }));
		}
	};

	return (
		<div className="flex max-w-2xl flex-col gap-6">
			<PageHeader
				back={<BackLink to="/product">{t("title")}</BackLink>}
				title={t("new.title")}
				description={t("new.description")}
				help="product:new"
			/>
			<Card>
				<CardContent>
					<Form form={form} onSubmit={onSubmit}>
						<SelectField
							control={form.control}
							name="kind"
							label={tBundle("kind.label")}
							help="product:kind"
							options={ProductKind.options.map((kind) => ({ value: kind, label: tBundle(`kind.${kind}`) }))}
						/>
						<TextField control={form.control} name="name" label={t("field.name")} autoComplete="off" />
						<div className="grid gap-5 sm:grid-cols-2">
							<TextField
								control={form.control}
								name="consumerPrice"
								label={t("field.consumerPrice")}
								help="price:consumerPrice"
								inputMode="decimal"
								autoComplete="off"
								placeholder="0,00"
							/>
							<SelectField
								control={form.control}
								name="vatRate"
								label={t("field.vatRate")}
								help="product:vatRate"
								options={VAT_RATES.map((rate) => ({
									value: String(rate),
									label: t("field.vatOption", { rate }),
								}))}
							/>
						</div>
						<TextareaField
							control={form.control}
							name="description"
							label={t("field.description")}
							rows={3}
						/>
						<details
							open={moreOpen || Boolean(errors.slug)}
							onToggle={(event) => setMoreOpen(event.currentTarget.open)}
							className="group flex flex-col gap-4"
						>
							<summary className="w-fit cursor-pointer text-sm text-muted-foreground select-none hover:text-foreground">
								{tCommon("more")}
							</summary>
							<div className="pt-4">
								<TextField
									control={form.control}
									name="slug"
									label={t("field.slug")}
									help="product:slug"
									description={t("field.slugDescription", { slug: slug || "…" })}
									autoComplete="off"
								/>
							</div>
						</details>
						<FormError control={form.control} />
						<div>
							<SubmitButton control={form.control}>{t("new.submit")}</SubmitButton>
						</div>
					</Form>
				</CardContent>
			</Card>
		</div>
	);
}
